import { Box, useMediaQuery, useTheme } from "@mui/material";
import { density, shape } from "@app/design-tokens";
import { useCallback, useState } from "react";
import { Outlet, useLocation } from "react-router";

import { useAuth } from "@/auth/authContext";
import { useUiStore } from "@/lib/uiStore";

import { MobileTabBar } from "./MobileTabBar";
import { activeSection } from "./navSections";
import { PageTitleSlotProvider } from "./pageTitleSlot";
import { SectionPanel } from "./SectionPanel";
import { ServicePageTabs } from "./ServicePageTabs";
import { ServiceRail } from "./ServiceRail";
import { TopBar } from "./TopBar";

/**
 * 整站的外殼。
 *
 * 桌機是「淺灰底 + 幾張浮在上面的白卡」：
 *
 *     ┌────┬──────────┬──────────────────────────────┐
 *     │rail│ 第二層   │  主內容卡（頂列 + 可滾動內容） │
 *     └────┴──────────┴──────────────────────────────┘
 *
 * 手機是類 App 的排版：沒有 rail，第一層變成底部的懸浮列，第二層不出現
 * （服務清單頁本身就是進入服務的入口）。服務內切換面向的 tab 兩邊共用同一組
 * UI，不是手機專屬的東西。
 *
 *     ┌──────────────────────────────┐
 *     │  頂列（標誌 + 深淺色 + 帳號） │
 *     │  面向 tab（在服務內才有）     │
 *     │  麵包屑 · 動作                │
 *     │  內容                        │
 *     │  ┌────────────────────────┐  │
 *     │  │ 總覽 服務 通知 個人中心 │  │ ← 懸浮
 *     └──┴────────────────────────┴──┘
 *
 * 三件刻意的決定：
 *
 * 1. **外殼不滾動，只有主內容卡的內容區滾動。** 所以導覽永遠在畫面上，
 *    長表格往下滾時不會把它捲走。
 * 2. **第二層的存在由「目前在哪一節」決定，不是使用者的偏好。** 總覽、通知、
 *    個人中心沒有第二層，切到那幾節時面板會收起來；切回服務又會回來。
 *    使用者手動收合的偏好（railExpanded）仍然被記住，只是它只對有第二層的
 *    分節有意義。
 * 3. **手機的底部列是 fixed 的**，所以外殼底部要留出它的高度，否則內容
 *    最後一列會被壓在下面。
 */
export function AppShell() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const location = useLocation();
  const { me } = useAuth();
  const railExpanded = useUiStore((state) => state.railExpanded);
  const toggleRail = useUiStore((state) => state.toggleRail);

  /**
   * 頂列上「頁面標題」那一格的 DOM 節點。
   *
   * 用 ref callback 而不是 useEffect 去抓 —— ref callback 在 commit 階段就
   * 被呼叫，接著的 re-render 仍在瀏覽器繪製之前完成，所以標題不會閃一下才
   * 出現。useCallback 是必要的：每次 render 都給新函式會讓 React 反覆
   * detach／attach 這個 ref。
   */
  const [titleSlot, setTitleSlot] = useState<HTMLElement | null>(null);
  const titleSlotRef = useCallback((node: HTMLElement | null) => setTitleSlot(node), []);

  const serviceCodes = (me?.services ?? []).map((service) => service.code);
  const section = activeSection(location.pathname, serviceCodes);
  const hasPanel = Boolean(section?.hasPanel);
  const showPanel = isDesktop && hasPanel && railExpanded;

  const gap = `${density.shellGap}px`;

  return (
    <Box
      sx={{
        height: "100dvh",
        bgcolor: "background.default",
        // 手機版模仿 Airbnb 行動網頁：內容貼齊螢幕邊緣，不留灰底的邊距，
        // 「浮在灰底上的白卡」只是桌機的排版語言。桌機才有這圈留白。
        // 懸浮導覽列要讓出的高度改留在下面 <main> 的 padding 裡（見那裡的
        // 註解）——不然這裡的 pb 會在最下面露出一段跟內容不同色的灰底，
        // 跟「貼齊邊緣」互相矛盾。
        p: isDesktop ? gap : 0,
        display: "flex",
        gap,
        overflow: "hidden",
      }}
    >
      {isDesktop && <ServiceRail />}

      {showPanel && section && (
        <Box
          component="aside"
          sx={{
            width: density.navPanelWidth,
            flex: "none",
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            borderRadius: `${shape.shell}px`,
            overflow: "hidden",
          }}
        >
          <SectionPanel section={section} />
        </Box>
      )}

      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          bgcolor: "background.paper",
          // 卡片的框線與圓角只在桌機有意義——手機沒有灰底可以「浮」在上面，
          // 邊框圓角在那裡只會憑空切掉一塊內容，所以手機版拿掉。
          ...(isDesktop
            ? { border: 1, borderColor: "divider", borderRadius: `${shape.shell}px` }
            : {}),
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/*
          「切換第二層」只有在這一節真的有第二層時才給 —— 按了不會有反應的
          按鈕比沒有按鈕更糟。手機版沒有第二層，所以改成顯示標誌。
        */}
        <TopBar
          {...(isDesktop && hasPanel ? { onToggleNav: toggleRail } : {})}
          showBrand={!isDesktop}
          titleSlotRef={titleSlotRef}
        />

        {/*
          服務內的面向 tab。桌機與手機共用同一組 UI，元件自己判斷要不要出現，
          所以這裡不需要任何條件。
        */}
        <ServicePageTabs />

        {/*
          內容區本身**不滾動**，而是一個高度已知的 flex column。
          這樣「表格自己滾、分頁列釘在卡片底部」才做得到 —— 若讓整個內容區
          滾動，長表格的分頁列會被推到畫面外，使用者得先滾到底才能換頁。
          需要整頁滾動的頁面（例如總覽）自己包一層 overflow: auto。
        */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            // 手機版底部列現在是貼底的系統列（見 MobileTabBar 的說明），
            // 這裡讓出跟它一樣高的空間——包含它自己的 safe-area-inset
            // ——內容才不會被蓋住。這段留白是內容自己的 padding，背景還是
            // 同一片白，不會露出另一條顏色不同的橫帶；頁面內主按鈕、表單
            // 最後一列因此不會被蓋住。
            pb: isDesktop ? 0 : `calc(${density.tabBarHeight}px + env(safe-area-inset-bottom, 0px))`,
          }}
        >
          <PageTitleSlotProvider value={titleSlot}>
            <Outlet />
          </PageTitleSlotProvider>
        </Box>
      </Box>

      {!isDesktop && <MobileTabBar />}
    </Box>
  );
}
